import React, { useEffect, useRef, useState } from "react";
import { Check, CircleAlert, Download, Plus, Search, X } from "lucide-react";
import { Pharmacy } from "../types";
import { Alert, Button, Drawer, Empty, ICON, IconButton, Input, Spinner } from "./ui";
import { DIAS, MESES, normalizar } from "./turnos";
import { adminFetch } from "../api";
import { generarHistoria } from "./historias/generar";

type SaveState =
  | { kind: "idle" }
  | { kind: "saving" }
  | { kind: "saved" }
  | { kind: "error"; message: string };

interface DayDrawerProps {
  /** Día abierto, "YYYY-MM-DD". */
  date: string;
  pharmacies: Pharmacy[];
  /** IDs de turno guardados para ese día (puede haber IDs que no están en el catálogo, P-3). */
  assignedIds: string[];
  onClose: () => void;
  /** Lanzan un Error con el motivo si el servidor no guardó. */
  onAdd: (pharmacyId: string) => Promise<void>;
  onRemove: (pharmacyId: string) => Promise<void>;
}

// React.FC para que TurnosPage pueda pasarle `key` (el proyecto no tiene @types/react)
export const DayDrawer: React.FC<DayDrawerProps> = ({ date, pharmacies, assignedIds, onClose, onAdd, onRemove }) => {
  const [query, setQuery] = useState("");
  const [save, setSave] = useState<SaveState>({ kind: "idle" });
  const lastAction = useRef<(() => Promise<void>) | null>(null);

  const run = async (action: () => Promise<void>) => {
    lastAction.current = action;
    setSave({ kind: "saving" });
    try {
      await action();
      setSave({ kind: "saved" });
    } catch (err) {
      const message = err instanceof Error && err.message ? err.message : "El servidor no respondió.";
      setSave({ kind: "error", message });
    }
  };
  const busy = save.kind === "saving";

  // IDs con foto de historia (null mientras carga) y estado de la descarga
  const [conFoto, setConFoto] = useState<Set<string> | null>(null);
  const [fotosError, setFotosError] = useState(false);
  const [bajando, setBajando] = useState<string | null>(null);
  const [bajarError, setBajarError] = useState<{ message: string; p: Pharmacy } | null>(null);

  useEffect(() => {
    let vivo = true;
    (async () => {
      try {
        const res = await adminFetch("/api/historia-fotos");
        if (!res.ok) throw new Error();
        const data = await res.json();
        if (vivo) setConFoto(new Set<string>(data.ids));
      } catch {
        if (vivo) {
          setConFoto(new Set());
          setFotosError(true);
        }
      }
    })();
    return () => {
      vivo = false;
    };
  }, []);

  const bajarHistoria = async (p: Pharmacy) => {
    setBajando(p.id);
    setBajarError(null);
    try {
      const res = await adminFetch("/api/historia-foto/" + encodeURIComponent(p.id));
      if (!res.ok) throw new Error("No se pudo traer la foto de la historia.");
      const foto = await res.blob();
      const { blob, nombreArchivo } = await generarHistoria(
        { fecha: date, nombre: p.nombre, direccion: p.direccion, telefono: p.telefono },
        foto,
      );
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = nombreArchivo;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (err) {
      const message = err instanceof Error && err.message ? err.message : "No se pudo armar la historia.";
      setBajarError({ message, p });
    } finally {
      setBajando(null);
    }
  };

  const [y, m, d] = date.split("-").map(Number);
  const dow = new Date(y, m - 1, d).getDay();
  const byId = new Map(pharmacies.map((p) => [p.id, p]));
  // Un ID que no está en el catálogo igual se muestra, para poder quitarlo
  const assigned = assignedIds.map(
    (id) => byId.get(id) || { id, nombre: id, direccion: "No está en el catálogo", telefono: "" },
  );
  const q = normalizar(query);
  const available = pharmacies.filter(
    (p) => !assignedIds.includes(p.id) && normalizar(p.nombre).includes(q),
  );

  const footer = (
    <>
      <span className="flex flex-1 items-center gap-1.5 text-[13px] text-adm-t55" role="status" aria-live="polite">
        {save.kind === "idle" && "Los cambios se guardan solos"}
        {save.kind === "saving" && (
          <>
            <Spinner />
            Guardando…
          </>
        )}
        {save.kind === "saved" && (
          <>
            <Check {...ICON} className="text-adm-acento" />
            Guardado
          </>
        )}
        {save.kind === "error" && (
          <span className="flex items-center gap-1.5 text-adm-rojo">
            <CircleAlert {...ICON} />
            No se guardó. Reintentá.
          </span>
        )}
      </span>
      <Button variant="primary" onClick={onClose}>
        Listo
      </Button>
    </>
  );

  return (
    <Drawer
      open
      onClose={onClose}
      eyebrow={DIAS[dow]}
      title={`${d} de ${MESES[m - 1]}`}
      subtitle="De 8:30 a 8:30 del día siguiente"
      footer={footer}
    >
      {save.kind === "error" && (
        <Alert
          tone="red"
          action={
            <Button size="sm" onClick={() => lastAction.current && run(lastAction.current)}>
              Reintentar
            </Button>
          }
        >
          {save.message}
        </Alert>
      )}

      {bajarError && (
        <Alert
          tone="red"
          action={
            <Button size="sm" onClick={() => bajarHistoria(bajarError.p)}>
              Reintentar
            </Button>
          }
        >
          {bajarError.message}
        </Alert>
      )}
      {fotosError && <Alert tone="amber">No se pudo revisar las fotos de historia.</Alert>}

      <div className="flex flex-col gap-2">
        <span className="text-xs font-medium text-adm-t55">De turno este día</span>
        <div className="flex flex-col gap-1">
          {assigned.length > 0 ? (
            assigned.map((p) => {
              const enCatalogo = byId.has(p.id);
              const sinFoto = enCatalogo && conFoto !== null && !conFoto.has(p.id);
              return (
              <div key={p.id} className="flex flex-col gap-1">
              <div
                className="flex h-14 items-center gap-3 rounded-xl border border-adm-acento-borde bg-adm-acento-suave pl-3.5 pr-1.5"
              >
                <div className="min-w-0 flex-1">
                  <div className="truncate text-sm font-medium text-adm-text notranslate" translate="no">
                    {p.nombre}
                  </div>
                  <div className="truncate text-xs text-adm-t55">{p.direccion}</div>
                </div>
                {enCatalogo && (
                  <IconButton
                    label={conFoto?.has(p.id) ? `Bajar historia de ${p.nombre}` : `${p.nombre}: falta la foto de historia`}
                    disabled={!conFoto?.has(p.id) || bajando !== null}
                    onClick={() => bajarHistoria(p)}
                  >
                    {bajando === p.id ? <Spinner /> : <Download {...ICON} />}
                  </IconButton>
                )}
                <IconButton
                  label={`Quitar ${p.nombre}`}
                  tone="danger"
                  disabled={busy}
                  onClick={() => run(() => onRemove(p.id))}
                >
                  <X {...ICON} />
                </IconButton>
              </div>
              {sinFoto && !fotosError && (
                <p className="px-3.5 text-xs text-adm-t55">Falta la foto de historia: cargala en Farmacias.</p>
              )}
              </div>
              );
            })
          ) : (
            <Empty size="sm" title="Ninguna todavía">
              Elegí abajo las farmacias de turno de este día.
            </Empty>
          )}
        </div>
      </div>

      <div className="flex flex-col gap-2">
        <span className="text-xs font-medium text-adm-t55">Agregar farmacia</span>
        <div className="relative">
          <Search
            {...ICON}
            className="pointer-events-none absolute left-3 top-1/2 z-[1] -translate-y-1/2 text-adm-t40"
          />
          <Input
            data-autofocus
            placeholder="Buscar farmacia"
            aria-label="Buscar farmacia"
            value={query}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setQuery(e.target.value)}
            className="pl-[38px]"
          />
        </div>
        <div className="flex flex-col gap-1">
          {available.map((p) => (
            <button
              key={p.id}
              type="button"
              disabled={busy}
              onClick={() => run(() => onAdd(p.id))}
              className="flex h-14 w-full cursor-pointer items-center gap-3 rounded-xl border border-transparent pl-3.5 pr-1.5 text-left transition-colors hover:bg-adm-t5 disabled:cursor-wait disabled:opacity-60"
            >
              <div className="min-w-0 flex-1">
                <div className="truncate text-sm font-medium text-adm-text notranslate" translate="no">
                  {p.nombre}
                </div>
                <div className="truncate text-xs text-adm-t55">{p.direccion}</div>
              </div>
              <Plus {...ICON} className="text-adm-acento-texto" />
            </button>
          ))}
          {available.length === 0 && (
            <p className="px-3.5 py-3 text-[13px] text-adm-t55">
              {query ? `Ninguna farmacia coincide con «${query}».` : "Ya están todas las farmacias del catálogo."}
            </p>
          )}
        </div>
      </div>
    </Drawer>
  );
};
